/**
 * NOTIF-003 — the bell's state: the unread summary (polled), the dropdown list, the user's
 * settings, and the "something new arrived" side effects (toast / sound / browser).
 *
 * Transport v1 = polling `unread-summary`; Reverb later swaps the trigger — `refresh()` is
 * the one seam. Polls run slower while the tab is hidden (browsers throttle timers anyway)
 * so a hidden tab can still raise a browser notification; a tab becoming visible refreshes
 * at once.
 *
 * NOTIF-011 — a row counts from its last state change (`touched_at`), so an export's row
 * rewritten as "ready" is announced again; while an export of mine is being prepared the
 * poll runs every few seconds (`expectExport`); an export row's click downloads the file —
 * through a signed link the browser opens itself (C2), one per part when there are several.
 *
 * NOTIF-012 — the real-time link (Reverb): the summary says whether and where to connect
 * (`realtime`); a push from the server runs the very same `refresh()`; while the socket is
 * up the timer only polls every few minutes as a safety net, and the export watch rests —
 * the moment it drops the usual polling is back. Nothing else changed: with the switch
 * off the store behaves exactly as before.
 */
import { defineStore } from 'pinia'
import {
  fetchExportDownloadLinks,
  fetchNotifications,
  fetchNotificationSettings,
  fetchNotificationTarget,
  fetchUnreadSummary,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationSettings,
  type NotificationItem,
  type NotificationLevel,
  type NotificationSettings,
  type NotificationSettingsOverview,
  type NotificationSettingsUpdate,
  type NotificationTarget,
  type NotificationTypeSetting,
  type RealtimeConfig,
  type UnreadSummary
} from '@/services/notificationsApi'
import { resolveApiBaseUrl } from '@/services/api'
import { announceNew } from '@/lib/notifications/alerts'
import {
  createEchoLink,
  realtimeLinkConfig,
  sameLinkConfig,
  type RealtimeLink,
  type RealtimeLinkFactory
} from '@/lib/notifications/realtime'
import {
  ExportDownloadError,
  absoluteExportUrl,
  activityAt,
  expiredExportTarget,
  exportOf,
  exportParts,
  exportRowState,
  freshRowLinks,
  isMultiPart,
  startBrowserDownload,
  type NotificationExport
} from '@/lib/notifications/exports'
import { LEVELS_READ_ON_SEEN, badgeLabel } from '@/lib/notifications/pages'
import { toast } from '@/services/toastService'

export const POLL_INTERVAL_VISIBLE_MS = 25_000
export const POLL_INTERVAL_HIDDEN_MS = 60_000
/** NOTIF-011 — while an export of mine is being prepared (a visible tab). */
export const POLL_INTERVAL_EXPORT_MS = 5_000
/** How long after an export was started the fast polling lasts at most. */
export const EXPORT_WATCH_MS = 10 * 60_000
/** NOTIF-012 — while the real-time link is up: a safety net, the pushes do the work. */
export const POLL_INTERVAL_LINKED_MS = 5 * 60_000
/** NOTIF-012 — pushes landing within this window share one refresh. */
export const PUSH_REFRESH_DELAY_MS = 250
/** How long an export's row says "Download started" after the file went to the browser. */
export const DOWNLOAD_STARTED_MS = 4_000
/** Newest unread rows a poll looks at to decide what to announce. */
export const ANNOUNCE_WINDOW = 10

function laterOf(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return b > a ? b : a
}

/**
 * NOTIF-007b — the announce mark a summary gives: the server's clock, or the newest unread row
 * when that is later (created while the summary was being built) or the clock is missing (an
 * older API). Everything created after the mark is news; a row read already and marked unread
 * again later is older than it.
 */
function markOf(summary: UnreadSummary): string | null {
  return laterOf(summary.as_of ?? null, summary.latest_at)
}

export const DEFAULT_SETTINGS: NotificationSettings = {
  toast_mode: 'action_needed',
  sound_mode: 'all',
  browser_enabled: true
}

function emptySummary(): UnreadSummary {
  return { total: 0, by_page_key: {}, by_level: {}, latest_at: null }
}

/**
 * Opens a deep link; set by the bell (router.push) so the store never imports the router.
 * Resolves true once the page is actually open — only then is the row read.
 */
let openHandler: (item: NotificationItem) => boolean | Promise<boolean> = () => false

export function setNotificationOpenHandler(
  handler: (item: NotificationItem) => boolean | Promise<boolean>
): void {
  openHandler = handler
}

/**
 * NOTIF-008 — shows the "record not found / deleted" modal; set by the dialog the bell mounts.
 * Resolves once the person closed it. Without a host nothing is shown.
 */
let deadLinkHandler: (target: NotificationTarget) => Promise<void> = async () => {}

export function setNotificationDeadLinkHandler(
  handler: (target: NotificationTarget) => Promise<void>
): void {
  deadLinkHandler = handler
}

/** "Open as before" — the target assumed when the check itself could not be made. */
function assumedOk(item: NotificationItem): NotificationTarget {
  return {
    state: 'ok',
    entity: null,
    label: null,
    deleted_at: null,
    deleted_by: null,
    notification: item
  }
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    summary: emptySummary() as UnreadSummary,
    summaryLoaded: false,
    summaryError: null as string | null,

    items: [] as NotificationItem[],
    itemsLoading: false,
    itemsLoaded: false,
    itemsError: null as string | null,
    /** The list filter the dropdown shows. */
    unreadOnly: true,

    settings: { ...DEFAULT_SETTINGS } as NotificationSettings,
    settingsLoaded: false,
    types: [] as NotificationTypeSetting[],
    availableChannels: [] as string[],

    /** High-water mark of `summary.latest_at` we have announced; null until the first poll. */
    announcedUpTo: null as string | null,

    polling: false,
    hidden: false,
    /** NOTIF-011 — until when (epoch ms) an export of mine may still be in flight: fast polling. */
    exportWatchUntil: 0,
    /** C2 — the exports (uuids) whose download links are being fetched: a second click waits. */
    downloading: [] as string[],
    /**
     * The exports (uuids) whose file just went to the browser: the row says "Download started"
     * for DOWNLOAD_STARTED_MS — the browser gives no signal of its own until its download bar
     * shows the file, and the panel / card no longer close on the click.
     */
    downloadStarted: [] as string[],
    /**
     * How many times each export's file(s) went to the browser (uuid → count): a card restarts
     * its own clock on every hand-off, which the list above cannot show while the moment lasts.
     */
    downloadHandOffs: {} as Record<string, number>,
    /** NOTIF-012 — the real-time socket is up: pushes arrive, the timer only guards. */
    linked: false
  }),

  getters: {
    unreadTotal(state): number {
      return state.summary.total
    },
    bellBadge(state): string {
      return badgeLabel(state.summary.total)
    },
    unreadForPage:
      (state) =>
      (pageKey: string): number =>
        state.summary.by_page_key[pageKey] ?? 0,
    unreadActionNeeded(state): number {
      return state.summary.by_level.action_needed ?? 0
    }
  },

  actions: {
    // ------------------------------------------------------------ polling

    /** Idempotent: the bell calls it on mount; a second bell / remount does not double the timer. */
    start(): void {
      if (this.polling) return
      this.polling = true
      this.hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      void this.loadSettings()
      void this.refresh()
      schedule(this)
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility)
        window.addEventListener('focus', onFocus)
      }
    },

    /**
     * Stop polling and forget everything: this is per-user state, and a logout / login in the
     * same tab must neither show the previous user's rows nor treat the next user's rows as
     * already seen. A poll still in flight drops its result.
     */
    stop(): void {
      if (this.polling) {
        clearTimer()
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibility)
          window.removeEventListener('focus', onFocus)
        }
      }
      closeLink()
      clearStartedTimers()
      pollGeneration++
      refreshInFlight = null
      this.$reset()
    },

    /**
     * One poll: read the summary, announce what arrived since the last poll. The very first
     * poll only sets the high-water mark — nothing that was already waiting is re-announced.
     * Concurrent calls (a tab coming back fires `visibilitychange` and `focus` together) share
     * one poll, so nothing is announced twice.
     */
    refresh(): Promise<void> {
      if (refreshInFlight === null) {
        const run: Promise<void> = this.poll().finally(() => {
          if (refreshInFlight === run) refreshInFlight = null
        })
        refreshInFlight = run
      }
      return refreshInFlight
    },

    /** The poll itself — always go through `refresh()`. */
    async poll(): Promise<void> {
      const generation = pollGeneration
      let next: UnreadSummary
      try {
        next = await fetchUnreadSummary()
        if (generation !== pollGeneration) return // stopped meanwhile — stale result
        this.summaryError = null
      } catch (err) {
        if (generation !== pollGeneration) return
        this.summaryError = err instanceof Error ? err.message : 'Failed to load notifications'
        return
      }

      const isFirst = !this.summaryLoaded
      this.summary = next
      this.summaryLoaded = true
      this.syncRealtime(next.realtime)

      // The mark moves with every poll to the server's clock (NOTIF-007b, see markOf): what is
      // created after it is news, a row marked unread again later is older than it.
      const since = this.announcedUpTo
      this.announcedUpTo = laterOf(since, markOf(next))
      // Baseline: whatever was already waiting shows in the badge, but is not announced.
      if (isFirst) return
      const latest = next.latest_at
      if (latest === null) return
      if (since !== null && latest <= since) return

      // Something arrived after `since` (or everything unread, when nothing had been seen).
      // A row counts from its last state change (NOTIF-011): an export rewritten as "ready" is news.
      try {
        const { items } = await fetchNotifications({ unread: true, perPage: ANNOUNCE_WINDOW })
        if (generation !== pollGeneration) return
        const fresh = since === null ? items : items.filter((i) => activityAt(i) > since)
        this.watchExports(fresh)
        if (fresh.length > 0) {
          announceNew(fresh, {
            settings: this.settings,
            hidden: this.hidden,
            // A browser-notification click is a click: open the page, then read the row.
            open: (item) => void this.open(item),
            // A full window means the burst may be larger than what we fetched.
            truncated: fresh.length >= ANNOUNCE_WINDOW
          })
          if (this.itemsLoaded) await this.loadItems()
        }
      } catch {
        /* the badge already moved; the announcement is best effort */
      }
    },

    /**
     * NOTIF-011 — an export of mine was just started (the page says so, or a "being prepared" row
     * arrived): poll every few seconds until it is ready, failed, or `EXPORT_WATCH_MS` passed.
     */
    expectExport(forMs: number = EXPORT_WATCH_MS): void {
      this.exportWatchUntil = Date.now() + forMs
      if (this.polling) schedule(this)
    },

    /**
     * NOTIF-012 — keep the real-time link in step with what the server says: open it when the
     * summary says so (again when the address or the channel changed), close it when the
     * switch went off or the store stopped. The same config → nothing to do.
     */
    syncRealtime(realtime: RealtimeConfig | null | undefined): void {
      const wanted = this.polling ? realtimeLinkConfig(realtime) : null
      if (sameLinkConfig(link?.config ?? null, wanted)) return
      closeLink()
      this.setLinked(false) // a replacement link reports its own connection
      if (wanted === null) return
      const generation = pollGeneration
      link = linkFactory(wanted, {
        onPush: () => {
          if (generation === pollGeneration) pushRefresh(this)
        },
        onState: (connected) => {
          if (generation === pollGeneration) this.setLinked(connected)
        }
      })
    },

    /**
     * The socket came up — refresh at once (pushes may have been missed while it was down) and
     * slow the timer down; it went down — the usual polling is back.
     */
    setLinked(connected: boolean): void {
      const was = this.linked
      this.linked = connected
      if (!this.polling || connected === was) return
      schedule(this)
      if (connected) void this.refresh()
    },

    /** The announced rows decide whether the fast polling goes on (a preparing export) or stops (none left). */
    watchExports(items: NotificationItem[]): void {
      const exports = items
        .map((i) => exportOf(i))
        .filter((e): e is NotificationExport => e !== null)
      if (exports.length === 0) return
      if (exports.some((e) => exportRowState(e) === 'preparing')) {
        this.expectExport()
      } else {
        this.exportWatchUntil = 0
        if (this.polling) schedule(this)
      }
    },

    // ------------------------------------------------------------ the list

    async loadItems(): Promise<void> {
      const generation = pollGeneration
      this.itemsLoading = true
      try {
        const { items } = await fetchNotifications({ unread: this.unreadOnly, perPage: 30 })
        if (generation !== pollGeneration) return
        this.items = items
        this.itemsLoaded = true
        this.itemsError = null
      } catch (err) {
        if (generation !== pollGeneration) return
        this.itemsError = err instanceof Error ? err.message : 'Failed to load notifications'
      } finally {
        if (generation === pollGeneration) this.itemsLoading = false
      }
    },

    async setUnreadOnly(value: boolean): Promise<void> {
      if (this.unreadOnly === value && this.itemsLoaded) return
      this.unreadOnly = value
      await this.loadItems()
    },

    /** The dropdown opened: show the list. Nothing is read by merely opening it (decision 2). */
    async openList(): Promise<void> {
      await this.loadItems()
    },

    /**
     * Decision 2 (amended): a Heads-up / Info row the user has actually looked at — the cursor
     * rested on it, it has focus, or it is in view on a touch device — counts as read.
     * action_needed is never "seen", only clicked. A no-op for rows already read.
     */
    async markSeen(item: NotificationItem): Promise<void> {
      const current = this.items.find((i) => i.id === item.id) ?? item // the row as it is now
      if (current.read_at !== null || !LEVELS_READ_ON_SEEN.includes(current.level)) return
      await this.markRead(current.id, current)
    },

    /**
     * A click (dropdown row, toast card or browser notification): first the server says whether
     * the record is still there (NOTIF-008 — a modal instead of a page that cannot show it; a
     * check that could not be made opens as before), then the deep link opens, and only once
     * the page is actually open is the row read (server). A navigation that was cancelled or
     * ended elsewhere leaves the row unread — it still waits in the panel.
     */
    async open(item: NotificationItem): Promise<void> {
      const generation = pollGeneration
      // NOTIF-011 — an export's row skips the check: the download's own answers say when the
      // file is gone, and a record that is gone is asked about only then (downloadExportParts).
      // One round trip less before the browser's own request.
      if (exportOf(item) !== null) {
        await this.openResolved(item, assumedOk(item), openHandler)
        return
      }
      let target = assumedOk(item)
      try {
        const answer = await fetchNotificationTarget(item.id)
        if (answer && typeof answer.state === 'string') target = answer
      } catch {
        /* the check failed — the link is followed as before */
      }
      if (generation !== pollGeneration) return
      await this.openResolved(item, target, openHandler)
    },

    /**
     * What a click does once the record's state is known: a dead link shows the modal and the
     * row is read when it is closed — nothing is left to do with it (owner, 2026-10-27); a live
     * one opens as before. `navigate` is the bell's push, or the open route's replace. True
     * when the row was dealt with (page open, or modal closed); false when the page did not open.
     */
    async openResolved(
      item: NotificationItem,
      target: NotificationTarget,
      navigate: (item: NotificationItem) => boolean | Promise<boolean>
    ): Promise<boolean> {
      const generation = pollGeneration
      if (target.state !== 'ok') {
        await this.deadLink(target)
        if (generation !== pollGeneration) return true
        await this.markRead(item.id, item)
        return true
      }
      const exp = exportOf(item)
      if (exp !== null) {
        return this.openExport(item, exp)
      }
      let opened = false
      try {
        opened = await navigate(item)
      } catch {
        /* the router threw — the page did not open */
      }
      if (!opened || generation !== pollGeneration) return false
      await this.markRead(item.id, item)
      return true
    },

    /** The dead-link modal; resolves once it is closed (at once when no host is mounted). */
    async deadLink(target: NotificationTarget): Promise<void> {
      await deadLinkHandler(target)
    },

    /**
     * NOTIF-011 — an export's row is not a page: a click downloads the file once it is ready
     * (the row is read when the download was handed to the browser), does nothing while the
     * export is being prepared or after it failed (the row itself says so), and shows "Export
     * expired on …" once the window closed — judged from the payload, and from the server's 410
     * when the payload has not caught up (then in the server's words: its date, or "the file is
     * no longer on the server"). Any other failure is a toast; the row stays unread.
     *
     * C2 — the browser downloads through a signed link: the one the row came with when it is
     * still fresh (NotificationResource mints a set at every read — no request at all before
     * the browser's own), else one minted at the click (fetchExportDownloadLinks). The file
     * appears in the browser's download bar at once, nothing goes through the tab's memory, and
     * a click while the links are still being fetched is ignored (`downloading`). An export of
     * several files is not downloaded from here: its Download is a dropdown (ExportDownloadMenu
     * → downloadExportPart / downloadExportAll).
     */
    async openExport(item: NotificationItem, exp: NotificationExport): Promise<boolean> {
      const state = exportRowState(exp)
      if (state === 'expired') {
        return this.expiredExport(item, exp)
      }
      if (state !== 'ready') return false
      if (isMultiPart(exp)) return false
      return this.downloadExportParts(item, exp, [1])
    },

    /** C2 — one part of a ready export (the dropdown's "Part N"). */
    async downloadExportPart(
      item: NotificationItem,
      exp: NotificationExport,
      part: number
    ): Promise<boolean> {
      if (exportRowState(exp) === 'expired') return this.expiredExport(item, exp)
      return this.downloadExportParts(item, exp, [part])
    },

    /** C2 — every part of a ready export (the dropdown's "Download all"), one browser download each. */
    async downloadExportAll(item: NotificationItem, exp: NotificationExport): Promise<boolean> {
      if (exportRowState(exp) === 'expired') return this.expiredExport(item, exp)
      return this.downloadExportParts(
        item,
        exp,
        exportParts(exp).map((file) => file.part)
      )
    },

    /**
     * The links the parts asked for are downloaded through: the row's own while they are fresh
     * and the export's window is not about to close (a path each — the API's origin goes in
     * front, as for `download-links`), else a set minted now by the server. Each with the
     * part's filename, for the browser's download name. A link handed to the browser is the
     * browser's from then on — the row reads as handed over, and a file gone from the disk
     * shows as the browser's own failed download (as it did after `download-links` too); the
     * server's refusals by state (expired, gone, not ready) are kept off that path by the
     * freshness rules, and meet the SPA through `download-links`, where they are handled.
     */
    async exportLinks(
      exp: NotificationExport,
      parts: number[]
    ): Promise<Array<{ part: number; url: string; filename: string }>> {
      const fresh = freshRowLinks(exp, parts)
      if (fresh !== null) {
        const files = exportParts(exp)
        return fresh.map((link) => ({
          part: link.part,
          url: absoluteExportUrl(link.url, resolveApiBaseUrl(), window.location.origin),
          filename:
            files.find((file) => file.part === link.part)?.filename ?? exp.filename ?? 'export'
        }))
      }
      return (await fetchExportDownloadLinks(exp.uuid)).parts
    },

    /** The record's state from the server, or null when the check could not be made. */
    async targetOf(item: NotificationItem): Promise<NotificationTarget | null> {
      try {
        const answer = await fetchNotificationTarget(item.id)
        return answer && typeof answer.state === 'string' ? answer : null
      } catch {
        return null
      }
    },

    /** C2 — is this export's download being handed to the browser right now? */
    isDownloading(uuid: string): boolean {
      return this.downloading.includes(uuid)
    },

    /** Did this export's file just go to the browser (the "Download started" moment)? */
    isDownloadStarted(uuid: string): boolean {
      return this.downloadStarted.includes(uuid)
    },

    /** The file went to the browser: "Download started" for DOWNLOAD_STARTED_MS, then back. */
    noteDownloadStarted(uuid: string): void {
      const pending = startedTimers.get(uuid)
      if (pending !== undefined) clearTimeout(pending)
      this.downloadHandOffs = {
        ...this.downloadHandOffs,
        [uuid]: (this.downloadHandOffs[uuid] ?? 0) + 1
      }
      if (!this.downloadStarted.includes(uuid)) {
        this.downloadStarted = [...this.downloadStarted, uuid]
      }
      startedTimers.set(
        uuid,
        setTimeout(() => {
          startedTimers.delete(uuid)
          this.downloadStarted = this.downloadStarted.filter((candidate) => candidate !== uuid)
        }, DOWNLOAD_STARTED_MS)
      )
    },

    /**
     * The links for the parts asked for, handed to the browser in order; the row is read once
     * the first one was. Refusals and failures as openExport() says.
     */
    async downloadExportParts(
      item: NotificationItem,
      exp: NotificationExport,
      parts: number[]
    ): Promise<boolean> {
      if (parts.length === 0) return false
      if (this.downloading.includes(exp.uuid)) return false // the last click is still being answered
      const generation = pollGeneration
      this.downloading = [...this.downloading, exp.uuid]
      let started = 0
      try {
        const links = await this.exportLinks(exp, parts)
        if (generation !== pollGeneration) return false
        for (const part of parts) {
          const link = links.find((candidate) => candidate.part === part)
          if (!link) {
            throw new Error(`Part ${part} of the export is not there.`)
          }
          // The browser refused the hand-over (a blocked download): the row stays unread so
          // the person can try again.
          if (!startBrowserDownload(link.url, link.filename)) {
            throw new Error('The download could not be started by the browser.')
          }
          started++
        }
      } catch (err) {
        if (generation !== pollGeneration) return false
        if (started === 0 && err instanceof ExportDownloadError && err.expired) {
          return this.expiredExport(item, exp, err.message)
        }
        if (started === 0 && err instanceof ExportDownloadError && err.status === 404) {
          // "Export not found": the record may be gone — the check skipped at the click is made
          // now, and a record that is gone gets the dead-link modal as any row would.
          const target = await this.targetOf(item)
          if (generation !== pollGeneration) return false
          if (target !== null && target.state !== 'ok') {
            await this.deadLink(target)
            if (generation !== pollGeneration) return true
            await this.markRead(item.id, item)
            return true
          }
        }
        try {
          toast.error(err instanceof Error ? err.message : 'The export could not be downloaded.')
        } catch {
          /* no toast container — the row stays unread, nothing else to do */
        }
        if (started === 0) return false
      } finally {
        this.downloading = this.downloading.filter((uuid) => uuid !== exp.uuid)
      }
      this.noteDownloadStarted(exp.uuid)
      await this.markRead(item.id, item)
      return true
    },

    /**
     * The "Export expired on …" modal (the server's sentence when it is the server that refused),
     * then the row is read — nothing is left to do with it.
     */
    async expiredExport(
      item: NotificationItem,
      exp: NotificationExport,
      reason: string | null = null
    ): Promise<boolean> {
      const generation = pollGeneration
      await this.deadLink(expiredExportTarget(item, exp, reason))
      if (generation !== pollGeneration) return true
      await this.markRead(item.id, item)
      return true
    },

    /**
     * Optimistic read. `clicked` is the row as the caller saw it — used for the badge
     * bookkeeping when the row is not in the loaded list (a browser-notification click
     * before the dropdown was ever opened).
     */
    async markRead(id: string, clicked?: NotificationItem): Promise<void> {
      const listed = this.items.find((i) => i.id === id)
      const item = listed ?? clicked
      if (item && item.read_at === null) {
        if (listed) {
          this.items = this.items.map((i) =>
            i.id === id ? { ...i, read_at: new Date().toISOString() } : i
          )
        }
        this.decrement(item)
      }
      try {
        await markNotificationRead(id)
      } catch {
        /* optimistic; the next poll corrects the badge */
      }
    },

    /** True once the server read them all; false when the request failed (the next poll corrects the badge). */
    async markAllRead(): Promise<boolean> {
      const generation = pollGeneration
      try {
        const { summary } = await markAllNotificationsRead()
        if (generation !== pollGeneration) return true
        this.applySummary(summary)
        const now = new Date().toISOString()
        this.items = this.items.map((i) => (i.read_at === null ? { ...i, read_at: now } : i))
        return true
      } catch {
        return false
      }
    },

    // ------------------------------------------------------------ settings

    async loadSettings(): Promise<void> {
      const generation = pollGeneration
      try {
        const overview = await fetchNotificationSettings()
        if (generation !== pollGeneration) return
        this.applyOverview(overview)
      } catch {
        /* defaults stay in force */
      }
    },

    async saveSettings(update: NotificationSettingsUpdate): Promise<NotificationSettingsOverview> {
      const generation = pollGeneration
      const overview = await updateNotificationSettings(update)
      if (generation === pollGeneration) this.applyOverview(overview)
      return overview
    },

    applyOverview(overview: NotificationSettingsOverview): void {
      this.settings = { ...DEFAULT_SETTINGS, ...overview.settings }
      this.types = overview.types
      this.availableChannels = overview.channels
      this.settingsLoaded = true
    },

    // ------------------------------------------------------------ internals

    /**
     * A summary returned by a write. The mark is only moved by refresh() — except when there is
     * none yet (the write landed before the first poll): then this summary is the baseline, so
     * the next poll announces nothing that was already there (CodeRabbit, NOTIF-007b rounds 1–2).
     */
    applySummary(summary: UnreadSummary): void {
      if (this.announcedUpTo === null) {
        this.announcedUpTo = markOf(summary)
      }
      this.summary = summary
      this.summaryLoaded = true
      this.syncRealtime(summary.realtime)
    },

    /** Local badge bookkeeping after an optimistic read. */
    decrement(item: NotificationItem): void {
      const s = this.summary
      const byPage = { ...s.by_page_key }
      if (item.page_key && byPage[item.page_key]) {
        byPage[item.page_key] = Math.max(0, byPage[item.page_key]! - 1)
        if (byPage[item.page_key] === 0) delete byPage[item.page_key]
      }
      const byLevel = { ...s.by_level }
      if (byLevel[item.level]) {
        byLevel[item.level] = Math.max(0, (byLevel[item.level] ?? 0) - 1)
        if (byLevel[item.level] === 0) delete byLevel[item.level]
      }
      this.summary = {
        ...s,
        total: Math.max(0, s.total - 1),
        by_page_key: byPage,
        by_level: byLevel
      }
    },

    /** Visibility changed: remember it, and a tab coming back refreshes right away. */
    setHidden(hidden: boolean): void {
      const wasHidden = this.hidden
      this.hidden = hidden
      if (!this.polling) return
      schedule(this)
      if (wasHidden && !hidden) void this.refresh()
    },

    /** Test seam: `stop()` already forgets everything; the production link factory is back. */
    resetForTests(): void {
      this.stop()
      linkFactory = createEchoLink
    }
  }
})

// ---------------------------------------------------------------- module-level timer

/** What the timer needs from the store (structural, so the store type never refers to itself). */
interface PollTarget {
  polling: boolean
  hidden: boolean
  /** NOTIF-011 — epoch ms until which an export of mine may be in flight (0: none). */
  exportWatchUntil: number
  /** NOTIF-012 — the real-time socket is up. */
  linked: boolean
  refresh(): Promise<void>
  setHidden(hidden: boolean): void
}

let timer: ReturnType<typeof setTimeout> | null = null
let current: PollTarget | null = null
/** The "Download started" moments still showing, by export uuid (noteDownloadStarted). */
const startedTimers = new Map<string, ReturnType<typeof setTimeout>>()

function clearStartedTimers(): void {
  for (const pending of startedTimers.values()) clearTimeout(pending)
  startedTimers.clear()
}
/** The poll in progress, shared by every `refresh()` call made while it runs. */
let refreshInFlight: Promise<void> | null = null
/**
 * Bumped by `stop()`; any request started under an older generation drops its result, so a
 * response that lands after a logout never writes into the reset (or the next user's) store.
 */
let pollGeneration = 0

// NOTIF-012 — the real-time link (one per store / session) and the factory that opens it.
let link: RealtimeLink | null = null
let linkFactory: RealtimeLinkFactory = createEchoLink
let pushTimer: ReturnType<typeof setTimeout> | null = null

/** Test seam: open links through this factory (a fake socket) instead of Echo. */
export function setRealtimeLinkFactory(factory: RealtimeLinkFactory | null): void {
  linkFactory = factory ?? createEchoLink
}

function closeLink(): void {
  if (pushTimer !== null) {
    clearTimeout(pushTimer)
    pushTimer = null
  }
  link?.disconnect()
  link = null
}

/** A push: one refresh for every push that lands within PUSH_REFRESH_DELAY_MS of the first. */
function pushRefresh(store: PollTarget): void {
  if (pushTimer !== null) return
  pushTimer = setTimeout(() => {
    pushTimer = null
    if (store.polling) void store.refresh()
  }, PUSH_REFRESH_DELAY_MS)
}

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

function schedule(store: PollTarget): void {
  current = store
  clearTimer()
  if (!store.polling) return
  // Socket up: the pushes do the work, the timer only guards. Down: as before (the export
  // watch included — a "ready" row then arrives by the fast poll again).
  const delay = store.linked
    ? POLL_INTERVAL_LINKED_MS
    : store.hidden
      ? POLL_INTERVAL_HIDDEN_MS
      : Date.now() < store.exportWatchUntil
        ? POLL_INTERVAL_EXPORT_MS
        : POLL_INTERVAL_VISIBLE_MS
  timer = setTimeout(() => {
    timer = null
    if (!store.polling) return
    void store.refresh().finally(() => schedule(store))
  }, delay)
}

function onVisibility(): void {
  current?.setHidden(document.visibilityState === 'hidden')
}

function onFocus(): void {
  if (current?.polling && !current.hidden) void current.refresh()
}

export type { NotificationItem, NotificationLevel, NotificationSettings, UnreadSummary }
